import { useOnboarding, tourSteps } from '@/contexts/OnboardingContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function OnboardingTour() {
  const { showTour, closeTour, currentStep, nextStep, prevStep, goToStep, totalSteps } = useOnboarding();

  const step = tourSteps[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const isLast = currentStep === totalSteps - 1;

  return (
    <Dialog open={showTour} onOpenChange={(open) => !open && closeTour()}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden border-primary/20 shadow-xl shadow-primary/10">
        {/* Progress bar */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground font-medium">
              {currentStep + 1} de {totalSteps}
            </span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={closeTour}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <Progress value={progress} className="h-1.5 transition-all duration-500" />
        </div>

        {/* Content */}
        <div className="px-6 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0.5, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 200, damping: 12 }}
                className="text-5xl mb-4 inline-block"
              >
                {step.icon}
              </motion.div>
              <DialogHeader>
                <DialogTitle className="text-xl mb-2">{step.title}</DialogTitle>
              </DialogHeader>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-muted-foreground text-sm leading-relaxed mt-2"
              >
                {step.description}
              </motion.p>
              {step.route && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary/80"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                  </span>
                  Navegando para esta tela
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-6 pb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>

          <div className="flex gap-1.5">
            {tourSteps.map((_, i) => (
              <motion.button
                key={i}
                onClick={() => goToStep(i)}
                aria-label={`Ir para passo ${i + 1}`}
                animate={{
                  width: i === currentStep ? 16 : 8,
                  backgroundColor: i === currentStep
                    ? 'hsl(var(--primary))'
                    : 'hsl(var(--muted-foreground) / 0.2)',
                }}
                whileHover={{ scale: 1.3 }}
                transition={{ duration: 0.3 }}
                className="h-2 rounded-full cursor-pointer hover:opacity-80"
              />
            ))}
          </div>

          <Button size="sm" onClick={nextStep} className="gap-1">
            {isLast ? 'Concluir' : 'Próximo'}
            {!isLast && <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
